import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { BookOpen } from 'lucide-react';
import {
  FormDialog,
  FormSection,
  FormSelectField,
  FormStatusField,
  FormTextField,
} from '@/components/common/FormDialog';
import { TAKA_SYMBOL } from '@/constants/app.constants';
import { bookEditionsQueryOptions, bookTypesQueryOptions } from '../api/product.api';
import { LOOKUP_PAGE_SIZE } from '../constants/product.constants';
import { useSaveSpecimen } from '../hooks/useCatalogueMutations';
import { specimenFormSchema, type Specimen, type SpecimenFormValues } from '../types/product.types';

const DEFAULTS: SpecimenFormValues = {
  barCode: '',
  name: '',
  nameLocal: '',
  description: '',
  netPrice: '',
  isActive: true,
};

const ALL_BOOK_TYPES_PARAMS = {
  page: 1,
  pageSize: LOOKUP_PAGE_SIZE,
} as const;

const ACTIVE_EDITIONS_PARAMS = {
  page: 1,
  pageSize: LOOKUP_PAGE_SIZE,
  isActive: true,
} as const;

interface SpecimenFormModalProps {
  specimen: Specimen | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SpecimenFormModal({ specimen, open, onOpenChange }: SpecimenFormModalProps) {
  const form = useForm<SpecimenFormValues>({
    resolver: zodResolver(specimenFormSchema),
    defaultValues: DEFAULTS,
  });

  const { data: bookTypes, isLoading: bookTypesLoading } = useQuery(
    bookTypesQueryOptions(ALL_BOOK_TYPES_PARAMS),
  );
  const { data: editions, isLoading: editionsLoading } = useQuery(
    bookEditionsQueryOptions(ACTIVE_EDITIONS_PARAMS),
  );

  useEffect(() => {
    form.reset(
      specimen === null
        ? DEFAULTS
        : {
            barCode: specimen.barCode ?? '',
            name: specimen.name,
            nameLocal: specimen.nameLocal ?? '',
            description: specimen.description ?? '',
            netPrice: specimen.netPrice === null ? '' : String(specimen.netPrice),
            isActive: specimen.isActive,
            ...(specimen.bookTypeNo === null ? {} : { bookTypeNo: specimen.bookTypeNo }),
            ...(specimen.bookEditionId === null ? {} : { bookEditionId: specimen.bookEditionId }),
          },
    );
  }, [specimen, open, form]);

  const save = useSaveSpecimen(() => onOpenChange(false));

  const bookTypeOptions = (bookTypes?.data ?? [])
    .filter((item) => item.legacyId !== null)
    .map((item) => ({ id: item.legacyId!, name: item.name }));

  const editionOptions = (editions?.data ?? []).map((item) => ({
    id: item.id,
    name: item.name,
  }));

  function submit(values: SpecimenFormValues) {
    const netPrice = values.netPrice.trim() === '' ? undefined : Number(values.netPrice);
    save.mutate({
      id: specimen?.id ?? null,
      payload: {
        barCode: values.barCode,
        name: values.name,
        isActive: values.isActive,
        ...(values.nameLocal.trim() === '' ? {} : { nameLocal: values.nameLocal.trim() }),
        ...(values.description.trim() === '' ? {} : { description: values.description.trim() }),
        ...(values.bookTypeNo === undefined ? {} : { bookTypeNo: values.bookTypeNo }),
        ...(values.bookEditionId === undefined ? {} : { bookEditionId: values.bookEditionId }),
        ...(netPrice === undefined || Number.isNaN(netPrice) ? {} : { netPrice }),
      },
    });
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={BookOpen}
      title={specimen === null ? 'Add Specimen' : 'Edit Specimen'}
      description="A book the field team hands out. The SP code is added when you save."
      form={form}
      isPending={save.isPending}
      isEditMode={specimen !== null}
      onSubmit={submit}
      width="wide"
    >
      <FormSection>
        <FormTextField
          control={form.control}
          name="name"
          label="Name"
          placeholder="12.Civics & Good Gov.-2"
          isFullRow
        />
        <FormTextField
          control={form.control}
          name="nameLocal"
          label="Local Name"
          placeholder="Optional"
          isFullRow
        />
      </FormSection>

      <FormSection>
        <FormSelectField
          control={form.control}
          name="bookTypeNo"
          label="Book Type"
          options={bookTypeOptions}
          isLoading={bookTypesLoading}
          placeholder="No book type"
        />
        <FormSelectField
          control={form.control}
          name="bookEditionId"
          label="Edition"
          options={editionOptions}
          isLoading={editionsLoading}
          placeholder="No edition"
        />
        <FormTextField
          control={form.control}
          name="barCode"
          label="Bar Code"
          placeholder="05160012112352269"
          hasTabularNumbers
        />
        <FormTextField
          control={form.control}
          name="netPrice"
          label="Net Price"
          placeholder="Optional"
          inputMode="decimal"
          leadingSymbol={TAKA_SYMBOL}
          hasTabularNumbers
        />
      </FormSection>

      <FormStatusField control={form.control} name="isActive" />
    </FormDialog>
  );
}
